// FORM_CONFIGS.userRevenueEntries.fields uzerine firms + bonusRevenueTypes
// listelerinden runtime options injection yapar.
//
// CiroTalepleriPage (duzenleme akisi) bu helper'i kullanir; CiroEklePage'in
// kendine ozgu "Tutar Getir" ek alanlari oldugu icin oradaki form fields
// inline kaliyor — fakat aynesi DRY etmek istenirse buradan turetilebilir.

import { FORM_CONFIGS } from '../constants/formConfigurations';
import {
  ONAY_SONRASI, isTubitakJob, resolveFillMode, findSelectedJob,
} from './revenueFillMode';

/**
 * @param {Object} options
 * @param {Array} options.firms - useFirms hook'undan gelen liste (nested
 *   contracts + jobs ile)
 * @param {Array} options.bonusRevenueTypes - useBonusRevenueTypes
 *   (applicable_channels barindirir)
 * @param {Array} [options.baseFields]
 *   - Default: FORM_CONFIGS.userRevenueEntries.fields, ozellestirme icin
 *     override edilebilir.
 * @returns {Array} firm_id / bonus_revenue_type_id / contract_id / job_id
 *   alanlarinda options injection'i yapilmis form fields.
 */
export const buildRevenueFormFields = ({
  firms,
  bonusRevenueTypes,
  baseFields,
} = {}) => {
  const firmsList = Array.isArray(firms) ? firms : [];
  const types = Array.isArray(bonusRevenueTypes) ? bonusRevenueTypes : [];
  const base = Array.isArray(baseFields)
    ? baseFields
    : FORM_CONFIGS.userRevenueEntries?.fields || [];

  const ciroTuruAll = types.map((b) => ({
    value: b.id,
    label: b.name || `#${b.id}`,
  }));

  const findFirm = (firmId) => {
    if (firmId == null) return undefined;
    return firmsList.find(
      (fm) => fm.id === parseInt(firmId, 10) || fm.id === firmId,
    );
  };

  return base
    .filter((f) => f.name !== 'companyId')
    .map((f) => {
      if (f.name === 'firm_id') {
        return {
          ...f,
          options: firmsList.map((fm) => ({
            value: fm.id,
            label: fm.firm_short_name || fm.firm_name || `Firma #${fm.id}`,
          })),
        };
      }
      if (f.name === 'bonus_revenue_type_id') {
        return {
          ...f,
          options: (formData) => {
            const kanal = formData?.ciro_kanali;
            if (!kanal) return ciroTuruAll;
            const filtered = types
              .filter((b) => {
                const arr = Array.isArray(b.applicable_channels)
                  ? b.applicable_channels
                  : [];
                return arr.includes(kanal);
              })
              .map((b) => ({ value: b.id, label: b.name || `#${b.id}` }));
            return filtered.length > 0 ? filtered : ciroTuruAll;
          },
        };
      }
      if (f.name === 'contract_id') {
        return {
          ...f,
          options: (formData) => {
            const selectedFirm = findFirm(formData?.firm_id);
            if (!selectedFirm?.contracts) return [];
            // Ayni sozlesme konusuna sahip birden fazla sozlesme olabilir —
            // aralarindan ayirt edebilmesi icin label'a imza tarihi eklendi.
            // job_id dropdown'unda ayni pattern zaten var.
            const fmtDate = (s) => {
              if (!s) return null;
              const d = new Date(s);
              if (Number.isNaN(d.getTime())) return null;
              return d.toLocaleDateString('tr-TR');
            };
            return selectedFirm.contracts.map((c) => {
              const subject = [c.contract_subject?.name, c.scope_of_work?.name]
                .filter(Boolean)
                .join(' / ') || `Sözleşme #${c.id}`;
              const signDate = fmtDate(c.contract_signing_date);
              const label = signDate
                ? `${subject} · Sözleşme: ${signDate}`
                : subject;
              return { value: c.id, label };
            });
          },
        };
      }
      if (f.name === 'job_id') {
        return {
          ...f,
          options: (formData) => {
            const selectedFirm = findFirm(formData?.firm_id);
            if (!selectedFirm?.jobs) return [];
            // Cok sayida sozlesme + is olan firmalarda karisikligi
            // onlemek icin her isin yaninda: proje no (varsa) + sozlesme
            // imza tarihi (varsa) + is kisa aciklamasi (varsa) gosteririz.
            // Tum kanallarda ayni davranis.
            const fmtDate = (s) => {
              if (!s) return null;
              const d = new Date(s);
              if (Number.isNaN(d.getTime())) return null;
              return d.toLocaleDateString('tr-TR');
            };
            return selectedFirm.jobs.map((j) => {
              const base = j.job_title || `İş #${String(j.id).slice(0, 8)}`;
              const extras = [];
              if (j.project_number) extras.push(`Proje No: ${j.project_number}`);
              const signDate = fmtDate(j.contract?.contract_signing_date);
              if (signDate) extras.push(`Sözleşme: ${signDate}`);
              const desc = (j.description_of_job || '').trim();
              if (desc) {
                const short = desc.length > 40 ? `${desc.slice(0, 40)}…` : desc;
                extras.push(short);
              }
              const label = extras.length > 0 ? `${base} — ${extras.join(' · ')}` : base;
              return { value: j.id, label };
            });
          },
        };
      }
      // OTOMATIK DOLDUR — ONAY SONRASI + TUBITAK'ta kapali.
      //
      // Ibrahim (kayit 22, 31:43/31:51): "Onay sonrasi TUBITAK'ta da
      // otomatik doldur gostermeyelim. Manuel doldur."
      //
      // Kural SADECE bu bilesime ait. Onay sonrasi ama TUBITAK olmayan
      // isler otomatik doldurabilmeye devam ediyor. FORM_CONFIGS'teki
      // tanimlar kuralsiz taban hal; is tipi oraya ulasmadigi icin
      // daraltma burada, firma listesi elimizdeyken yapiliyor.
      if (f.name === 'fill_mode') {
        return {
          ...f,
          showIf: (formData) => formData?.ciro_kanali === ONAY_SONRASI
            && !isTubitakJob(findSelectedJob(firmsList, formData)),
        };
      }
      // Secili Aylar yalnizca gercekten auto modda anlamli. Kural
      // kapaliyken fill_mode kayitli degeri 'auto' olsa bile gizlenir.
      // Ay Sayisi (x): taban kosul ayni, yalnizca mod ortak kurala
      // baglaniyor — boylece onay sonrasi TUBITAK'ta ('manual') alan
      // acik olur, diger isler eskisi gibi davranir.
      if (f.name === 'ay_sayisi') {
        return {
          ...f,
          showIf: (formData, currentUser) => formData?.ciro_kanali === ONAY_SONRASI
            && (
              currentUser?.role === 'ROOT'
              || resolveFillMode(formData, findSelectedJob(firmsList, formData)) === 'manual'
            ),
        };
      }
      if (f.name === 'secili_aylar') {
        return {
          ...f,
          showIf: (formData) => formData?.ciro_kanali === ONAY_SONRASI
            && resolveFillMode(formData, findSelectedJob(firmsList, formData)) === 'auto',
        };
      }
      return f;
    });
};
